import { Helmet } from '@dr.pogodin/react-helmet';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router';
import HashLink from '../common/lib/HashLink';
import Loading from '../common/lib/Loading';
import PageNotFound from '../common/lib/PageNotFound';
import XoopsCode from '../common/lib/XoopsCode';
import type { MultiLang } from '../config';
import Functions from '../functions';
import type PicoContext from './lib/PicoContext';
import type { PicoCategoryData, PicoPageData } from './lib/PicoContext';

interface Props {
    lang: MultiLang;
    context: PicoContext;
    contentId: number;
}

interface LoadedPage {
    context: PicoContext;
    contentId: number;
    page: PicoPageData | null;
}

const PicoContent = (props: Props) => {
    const { lang, context, contentId } = props;
    const [loaded, setLoaded] = useState<LoadedPage | null>(null);

    useEffect(() => {
        let active = true;
        context.getPage(contentId).then((page) => {
            if (active) {
                setLoaded({ context, contentId, page });
            }
        });
        return () => {
            active = false;
        };
    }, [context, contentId]);

    // A page loaded for the previous content must not be shown for this one.
    if (loaded === null || loaded.context !== context || loaded.contentId !== contentId) {
        return <Loading />;
    }
    const { page } = loaded;
    if (page === null) {
        return <PageNotFound lang={lang} />;
    }
    const pico = context.getModule();
    if (pico === null) {
        return <PageNotFound lang={lang} />;
    }
    const categories = context.getContentCategery(contentId);
    return (
        <div className="pico_container">
            <Helmet>
                <title>
                    {Functions.mlang(page.title, lang)} - {Functions.mlang(pico.name, lang)} -{' '}
                    {Functions.siteTitle(lang)}
                </title>
            </Helmet>
            {pico.show_breadcrumbs !== 0 && (
                <div className="pico_breadcrumbs">
                    {categories.map((category: PicoCategoryData, idx: number) => (
                        <Fragment key={category.id}>
                            {idx > 0 && <>&nbsp;&gt;&nbsp;</>}
                            <Link to={context.getUrl(category.link)}>{Functions.mlang(category.title, lang)}</Link>
                        </Fragment>
                    ))}
                    &nbsp;&gt;&nbsp;{Functions.mlang(page.title, lang)}
                </div>
            )}
            <div id="top_of_pico_body" className="pico_body">
                <XoopsCode lang={lang} text={page.content[lang]} dohtml={true} />
            </div>
            <div className="bottom_of_content_body">
                <HashLink to="#top_of_pico_body">
                    {Functions.mlang('[en]Jump to the top[/en][ja]この記事の1行目に飛ぶ[/ja]', lang)}
                </HashLink>
            </div>
        </div>
    );
};

export default PicoContent;
